import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, Globe, Check, X, ShoppingCart, Loader2, Star, ArrowRight, Shield, RefreshCw, Clock, ArrowRightLeft, Key, Plus } from 'lucide-react';
import { RevealWrapper } from '../hooks/useReveal';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const DomainsPage = () => {
  const navigate = useNavigate();
  const { addDomain, hasDomain, setIsCartOpen } = useCart();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [tlds, setTlds] = useState([]);
  const [popularTlds, setPopularTlds] = useState([]);
  const [loadingTlds, setLoadingTlds] = useState(true);
  
  // Transfer dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferDomain, setTransferDomain] = useState(null);
  const [authCode, setAuthCode] = useState('');

  // Load TLD prices
  useEffect(() => {
    const loadTlds = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/domains/tlds`);
        if (res.ok) {
          const data = await res.json();
          setTlds(data.tlds || []);
          setPopularTlds(data.popular || []);
        }
      } catch (e) {
        console.error('TLD load error:', e);
      } finally {
        setLoadingTlds(false);
      }
    };
    loadTlds();
  }, []);

  const searchDomain = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setResults(null);

    try {
      const res = await fetch(`${backendUrl}/api/domains/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: searchQuery.trim().toLowerCase() })
      });
      if (res.ok) {
        const data = await res.json();
        const allResults = data.results || [];
        const main = allResults.length > 0 ? allResults[0] : null;
        const alternatives = allResults.slice(1);
        const studio = data.suggestions || [];
        setResults({ main, alternatives, studio });
      } else {
        toast.error('Domain-Suche fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Verbindungsfehler');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  // Add domain to cart (uses global cart context)
  const handleAddToCart = (domain, type = 'registration', authCodeValue = '') => {
    const tld = '.' + domain.domain.split('.').slice(1).join('.');
    const tldInfo = tlds.find(t => t.tld === tld);
    const price = type === 'transfer' 
      ? tldInfo?.price_transfer 
      : (domain.price_registration || domain.price || tldInfo?.price_registration);
    
    addDomain({
      domain: domain.domain,
      type,
      price,
      years: 1,
      authCode: authCodeValue
    });
    
    toast.success(`${domain.domain} zum Warenkorb hinzugefügt`);
    
    if (type === 'transfer') {
      setTransferDialogOpen(false);
    }
  };

  // Transfer dialog handlers
  const handleTransferClick = (domain) => {
    setTransferDomain(domain);
    setAuthCode('');
    setTransferDialogOpen(true);
  };

  const handleAddTransfer = () => {
    if (!authCode.trim()) {
      toast.error('Bitte Auth-Code eingeben');
      return;
    }
    handleAddToCart(transferDomain, 'transfer', authCode.trim());
  };

  // Get transfer price for a domain
  const getTransferPrice = (domain) => {
    if (!domain) return null;
    const tld = '.' + domain.domain.split('.').slice(1).join('.');
    const tldInfo = tlds.find(t => t.tld === tld);
    return tldInfo?.price_transfer;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  const popularTldPrices = tlds
    .filter(t => popularTlds.includes(t.tld))
    .sort((a, b) => popularTlds.indexOf(a.tld) - popularTlds.indexOf(b.tld));

  return (
    <div className="min-h-screen bg-background">
      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-primary" />
              Domain transferieren
            </DialogTitle>
            <DialogDescription>
              Übertrage <strong>{transferDomain?.domain}</strong> zu Wolkenhelden
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">{transferDomain?.domain}</span>
                <span className="font-bold">{formatPrice(getTransferPrice(transferDomain))} / Jahr</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Inkl. 1 Jahr Verlängerung, DNS-Verwaltung & WHOIS-Schutz
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="authCode" className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                Auth-Code / EPP-Code
              </Label>
              <Input
                id="authCode"
                placeholder="Den Auth-Code erhältst du vom aktuellen Anbieter"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                data-testid="transfer-auth-code-input"
              />
              <p className="text-xs text-muted-foreground">
                Fordere den Auth-Code bei deinem aktuellen Domain-Anbieter an.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleAddTransfer} 
              disabled={!authCode.trim()}
              data-testid="add-transfer-to-cart-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              In den Warenkorb
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background py-20 lg:py-28">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <RevealWrapper>
            <Badge variant="outline" className="mb-4 text-primary border-primary/30">
              <Globe className="h-3 w-3 mr-1" /> Domain-Registrierung & Transfer
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
              Deine perfekte Domain
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Registriere eine neue Domain oder transferiere deine bestehende Domain zu uns – über 500 TLDs verfügbar.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <Check className="h-3 w-3 mr-1 text-green-500" /> Neue Domain registrieren
              </Badge>
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <ArrowRightLeft className="h-3 w-3 mr-1 text-blue-500" /> Bestehende Domain transferieren
              </Badge>
              <Badge variant="secondary" className="px-4 py-2 text-sm">
                <Shield className="h-3 w-3 mr-1 text-purple-500" /> Inkl. DNS-Verwaltung
              </Badge>
            </div>

            {/* Search Box */}
            <div className="flex gap-2 max-w-xl mx-auto">
              <Input
                placeholder="Domain eingeben, z.B. meinefirma.de"
                className="h-12 text-base"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchDomain()}
                data-testid="domain-search-input"
              />
              <Button 
                onClick={searchDomain} 
                disabled={searching || !searchQuery.trim()}
                className="h-12 px-6"
                data-testid="domain-search-btn"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Prüfen
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3">
              Gib deine Wunschdomain ein – wir prüfen die Verfügbarkeit und zeigen dir Optionen zum Registrieren oder Transferieren.
            </p>
          </RevealWrapper>
        </div>
      </section>

      {/* Results */}
      {results && (
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-xl font-semibold mb-4">Suchergebnisse</h2>
            
            {/* Main Result */}
            {results.main && (
              <Card className={`mb-4 ${results.main.available ? 'border-green-500/50 bg-green-50/30 dark:bg-green-950/20' : 'border-red-500/30 dark:border-red-500/20'}`}>
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {results.main.available ? (
                      <div className="h-10 w-10 rounded-full bg-green-500/10 dark:bg-green-500/20 flex items-center justify-center">
                        <Check className="h-5 w-5 text-green-500 dark:text-green-400" />
                      </div>
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-red-500/10 dark:bg-red-500/20 flex items-center justify-center">
                        <X className="h-5 w-5 text-red-500 dark:text-red-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-lg">{results.main.domain}</p>
                      <p className="text-sm text-muted-foreground">
                        {results.main.available ? 'Verfügbar' : 'Bereits vergeben – Transfer möglich'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {results.main.available && (results.main.price_registration || results.main.price) && (
                      <>
                        <span className="text-lg font-bold">{formatPrice(results.main.price_registration || results.main.price)} / Jahr</span>
                        <Button 
                          onClick={() => handleAddToCart(results.main, 'registration')} 
                          disabled={hasDomain(results.main.domain)}
                          data-testid="register-domain-btn"
                        >
                          {hasDomain(results.main.domain) ? (
                            <><Check className="h-4 w-4 mr-2" /> Im Warenkorb</>
                          ) : (
                            <><Plus className="h-4 w-4 mr-2" /> In den Warenkorb</>
                          )}
                        </Button>
                      </>
                    )}
                    {results.main.available && !results.main.price_registration && !results.main.price && (
                      <Button 
                        onClick={() => handleAddToCart(results.main, 'registration')}
                        disabled={hasDomain(results.main.domain)}
                      >
                        {hasDomain(results.main.domain) ? (
                          <><Check className="h-4 w-4 mr-2" /> Im Warenkorb</>
                        ) : (
                          <><Plus className="h-4 w-4 mr-2" /> In den Warenkorb</>
                        )}
                      </Button>
                    )}
                    {!results.main.available && (
                      <>
                        <span className="text-sm text-muted-foreground">
                          Transfer: {formatPrice(getTransferPrice(results.main))} / Jahr
                        </span>
                        <Button 
                          variant="outline" 
                          onClick={() => handleTransferClick(results.main)} 
                          disabled={hasDomain(results.main.domain)}
                          data-testid="transfer-domain-btn"
                        >
                          {hasDomain(results.main.domain) ? (
                            <><Check className="h-4 w-4 mr-2" /> Im Warenkorb</>
                          ) : (
                            <><ArrowRightLeft className="h-4 w-4 mr-2" /> Transferieren</>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alternatives */}
            {results.alternatives && results.alternatives.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground mt-6 mb-3">Weitere Vorschläge</h3>
                {results.alternatives.filter(a => a.available).map((alt) => (
                  <Card key={alt.domain} className="glass-card hover:border-primary/30 transition-colors">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                        <span className="font-medium">{alt.domain}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        {(alt.price_registration || alt.price) && <span className="font-semibold">{formatPrice(alt.price_registration || alt.price)} / Jahr</span>}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleAddToCart(alt, 'registration')} 
                          disabled={hasDomain(alt.domain)}
                        >
                          {hasDomain(alt.domain) ? (
                            <><Check className="h-4 w-4 mr-1" /> Hinzugefügt</>
                          ) : (
                            <><Plus className="h-4 w-4 mr-1" /> Hinzufügen</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Studio/Premium suggestions */}
            {results.studio && results.studio.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground mt-6 mb-3">
                  <Star className="h-4 w-4 inline mr-1 text-amber-500 dark:text-amber-400" />
                  Premium-Vorschläge
                </h3>
                {results.studio.filter(s => s.available).map((sug) => (
                  <Card key={sug.domain} className="glass-card hover:border-amber-500/30 dark:hover:border-amber-400/30 transition-colors">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Star className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                        <span className="font-medium">{sug.domain}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        {(sug.price_registration || sug.price) && <span className="font-semibold">{formatPrice(sug.price_registration || sug.price)} / Jahr</span>}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleAddToCart(sug, 'registration')} 
                          disabled={hasDomain(sug.domain)}
                        >
                          {hasDomain(sug.domain) ? (
                            <><Check className="h-4 w-4 mr-1" /> Hinzugefügt</>
                          ) : (
                            <><Plus className="h-4 w-4 mr-1" /> Hinzufügen</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Transfer Info Box - always visible in results */}
            <Card className="mt-6 bg-blue-50/50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                    <ArrowRightLeft className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100">Domain bereits woanders registriert?</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      Transferiere deine bestehende Domain zu Wolkenhelden – inkl. 1 Jahr Verlängerung, DNS-Verwaltung und WHOIS-Schutz.
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      Gib einfach deine Domain oben ein. Bei bereits vergebenen Domains siehst du die Transfer-Option.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Popular TLDs & Transfer Info - shown before search */}
      {!results && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            {/* Two column layout */}
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Popular TLDs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-green-500" />
                    Neue Domain registrieren
                  </CardTitle>
                  <CardDescription>Beliebte Endungen für deine neue Domain</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {popularTldPrices.slice(0, 8).map(tld => (
                      <div key={tld.tld} className="flex justify-between items-center p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <span className="font-mono font-semibold text-primary">{tld.tld}</span>
                        <span className="text-sm text-muted-foreground">ab {formatPrice(tld.price_registration)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Transfer Info */}
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 border-blue-200 dark:border-blue-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                    Domain transferieren
                  </CardTitle>
                  <CardDescription>Bringe deine bestehende Domain zu uns</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Du hast bereits eine Domain bei einem anderen Anbieter? Transferiere sie zu Wolkenhelden und profitiere von:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                      <span>1 Jahr Verlängerung inklusive</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                      <span>Kostenlose DNS-Verwaltung</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                      <span>WHOIS-Privatsphäre</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                      <span>Günstige Transfer-Preise</span>
                    </li>
                  </ul>
                  <div className="pt-2">
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      <Key className="h-3 w-3 inline mr-1" />
                      Du benötigst den Auth-Code von deinem aktuellen Anbieter.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="text-center p-6">
              <Shield className="h-10 w-10 mx-auto mb-4 text-primary" />
              <h3 className="font-semibold mb-2">WHOIS-Schutz</h3>
              <p className="text-sm text-muted-foreground">Deine Daten bleiben privat</p>
            </Card>
            <Card className="text-center p-6">
              <RefreshCw className="h-10 w-10 mx-auto mb-4 text-primary" />
              <h3 className="font-semibold mb-2">Auto-Verlängerung</h3>
              <p className="text-sm text-muted-foreground">Deine Domain läuft nie ab</p>
            </Card>
            <Card className="text-center p-6">
              <Globe className="h-10 w-10 mx-auto mb-4 text-primary" />
              <h3 className="font-semibold mb-2">DNS-Verwaltung</h3>
              <p className="text-sm text-muted-foreground">Volle Kontrolle über deine DNS-Einträge</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA for Cloud */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <Card className="max-w-2xl mx-auto p-8 bg-primary/5 border-primary/20">
            <h3 className="text-2xl font-bold mb-4">Domain + Cloud kombinieren</h3>
            <p className="text-muted-foreground mb-6">
              Sichere dir deine Domain direkt mit einer Wolkenhelden Cloud – alles aus einer Hand.
            </p>
            <Button size="lg" onClick={() => navigate('/preise')}>
              Cloud konfigurieren
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export { DomainsPage };
